import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, Fragment, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useElementHeight } from "../../../../hooks/useElementHeight";
import { INK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import type { Job } from "../../../../utils/models";
import { InHouseTooltip } from "../../../InHouseTooltip/InHouseTooltip";
import { activateOnKey } from "./a11y";
import { MonthlyJobCell } from "./MonthlyJobCell";
import {
	DAYS_IN_WEEK,
	MONTHLY_MIN_MEASURED_ROW_HEIGHT,
	type MonthlyDayFilters,
	type MonthlyShiftWindow,
	type MonthlyVisibleDay,
	nurseWorksDay,
	resolveCellDensity,
	type StaffingTotalsMode,
} from "./monthlyJobUtils";

const WEEK_LABEL_WIDTH = 132;
/** `gap: 0.5` on the rows container, in px (MUI's 8px spacing unit). */
const ROW_GAP_PX = 4;
/**
 * A month-divider row: 13px label + `pt: 0.5` + its 1px top border, rounded up.
 *
 * This is a reservation the density math subtracts from the height the week rows divide, so it has to be
 * TRUE, not approximate: under-reserve it and every cell is budgeted a row it does not have. A fixed
 * number over a flex row that could wrap is a guess — one wrapped label and the real divider is 2x this,
 * quietly stealing a row's worth of height from six cells. So the divider below is pinned to one line
 * (`nowrap` + an ellipsis on the secondary label), which is what makes this constant a fact instead of an
 * estimate. Text-sized either way, so it never varies with cell density and cannot feed the measurement
 * loop `density` is careful to avoid.
 */
const MONTH_DIVIDER_HEIGHT_PX = 26;

type Props = {
	/**
	 * The rendered week rows, already resolved through `getMonthlyVisibleWeek` (week-start rotation and
	 * the trailing spill included). Resolved by the state layer so the derived shift-time windows and
	 * these cells are read off exactly the same 42 days.
	 */
	visibleWeeks: { weekIndex: number; days: MonthlyVisibleDay[] }[];
	visibleJobs: Job[];
	/** The department's derived shift-time windows — one set for the whole rendered span. */
	windows: readonly MonthlyShiftWindow[];
	totalsMode: StaffingTotalsMode;
	filters: MonthlyDayFilters;
	today: DateTime;
	isPastDay: (date: DateTime) => boolean;
	/** Auto-scheduler departments don't post shifts — threaded down so the cells' Post action is gated. */
	isAutoScheduler: boolean;
	/** When set, cells where this nurse works are ringed (Search Staff highlight — never a filter). */
	highlightNurseId?: number;
	/** Receives SOURCE (Sunday-indexed) coordinates, so the sidebar can read `weeks` directly. */
	onOpenSidebar: (sourceWeekIndex: number, sourceDayIndex: number, date: DateTime, focusJobId?: string) => void;
	/** Leaves the month for one day's full Day view — the tile's arrow, and only the arrow. */
	onOpenDayView: (date: DateTime) => void;
	onWeekNumberClick: (weekIndex: number) => void;
};

export const MonthlyJobGrid: FC<Props> = ({
	visibleWeeks,
	visibleJobs,
	windows,
	totalsMode,
	filters,
	today,
	isPastDay,
	isAutoScheduler,
	highlightNurseId,
	onOpenSidebar,
	onOpenDayView,
	onWeekNumberClick,
}) => {
	const { t } = useTranslation();

	// How much a cell can hold is a MEASURED question, not a guess: the six rows divide whatever height is
	// left after the chrome, so the same cell is ~150px at 1080p and ~104px on a 1440x800 laptop.
	//
	// Measure the rows CONTAINER, never a row. The container's height is viewport-driven and bounded by its
	// own `flex: 1; minHeight: 0`, so it does not react to cell content. A row does: rows carry
	// `min-height: auto` (so they can't shrink below the cells' legibility floor), which lets content push a
	// row past its `flex: 1 1 0` share. Observing a row therefore closes a feedback loop — measure row ->
	// density -> cells render more/fewer window rows -> row height changes -> observer fires -> new density
	// -> ... Each turn is a fresh ResizeObserver task rather than one synchronous cascade, so React never
	// raises "Maximum update depth" and nothing appears in the console; the main thread simply spins and the
	// tab freezes. Deriving the per-row height arithmetically from the container keeps the input independent
	// of the output. A few px of error is harmless: `resolveCellDensity` floors into 1-4 rows.
	const rowsContainerRef = useRef<HTMLDivElement | null>(null);
	const containerHeight = useElementHeight(rowsContainerRef);
	const density = useMemo(() => {
		const rowCount = visibleWeeks.filter((week) => week.days.length > 0).length;
		if (rowCount === 0 || containerHeight === 0) return resolveCellDensity(0);
		// Dividers are text-sized and density-independent, so subtracting them cannot reintroduce the loop.
		const dividerCount = visibleWeeks.filter((week, position) => position > 0 && week.days.some((day) => day.date.day === 1)).length;
		const spacing = ROW_GAP_PX * Math.max(0, rowCount + dividerCount - 1) + MONTH_DIVIDER_HEIGHT_PX * dividerCount;
		// Clamped to a positive floor: `resolveCellDensity` reads `0` as "nobody has measured yet" and
		// answers with the assumed 130px, so an over-constrained grid whose budget comes out at or below
		// zero would be handed the TALLEST density on the tightest cell it could possibly have. Zero stays
		// reserved for the genuinely unmeasured case above.
		return resolveCellDensity(Math.max((containerHeight - spacing) / rowCount, MONTHLY_MIN_MEASURED_ROW_HEIGHT));
	}, [containerHeight, visibleWeeks]);

	// Weekday labels come from the RESOLVED dates of the first rendered week, which is where the
	// department's configured first-day-of-week actually shows up: the backend's `days[]` is always
	// Sunday-indexed, so labelling straight off it printed SUN..SAT for every department.
	const weekdayLabels = useMemo(() => visibleWeeks[0]?.days.map((day) => day.date.toFormat("ccc").toUpperCase()) ?? [], [visibleWeeks]);

	return (
		/*
		 * A real `grid`, not a stack of divs that happen to look like one.
		 *
		 * The day tile is a COMPOSITE control: one tab stop that owns several actions (the open-day arrow,
		 * post/add/remove), reached by keyboard shortcut rather than by making 42 cells into 126 tab stops.
		 * It used to declare itself `role="button"`, which cannot host interactive descendants — a button's
		 * content is presentational, so the action buttons inside it produced an invalid tree that no amount
		 * of event handling could repair. `gridcell` is the role that legitimately holds them, and it only
		 * exists inside a `grid` → `row` → cell structure, so the structure is what has to be declared.
		 *
		 * `rowgroup` on the stretching container is what lets the weekday header stay a sibling row here
		 * while the week rows live inside their own flex box (they need it — see the density note above).
		 */
		<Box
			role="grid"
			aria-label={t("monthly.view.grid.label")}
			aria-colcount={DAYS_IN_WEEK + 1}
			sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0, width: "100%", overflowY: "auto" }}
		>
			<Box
				role="row"
				sx={{
					display: "grid",
					gridTemplateColumns: `${WEEK_LABEL_WIDTH}px repeat(${DAYS_IN_WEEK}, 1fr)`,
					// Sticky against this component's own scroll box, and kept OUT of the stretching rows
					// container below so it never claims a share of the available height.
					flex: "0 0 auto",
					position: "sticky",
					top: 0,
					zIndex: 1,
					backgroundColor: SURFACE,
				}}
			>
				{/* The week-label column's header. Empty, but it still has to BE a column header, or the
				    header row is one cell short of every week row and the column indices stop lining up. */}
				<Box role="columnheader" aria-label={t("monthly.view.week.column.header")} />
				{weekdayLabels.map((label, index) => (
					<Typography
						// biome-ignore lint/suspicious/noArrayIndexKey: fixed 7-column header, index IS the column.
						key={`${label}-${index}`}
						role="columnheader"
						sx={{ textAlign: "center", fontSize: "12px", fontWeight: 500, color: INK.SECONDARY, py: 0.5 }}
					>
						{label}
					</Typography>
				))}
			</Box>

			{/* The single stretching container: week rows divide whatever height is left over. This is also
			    the element the density measurement observes — its height is viewport-driven, not content-driven. */}
			<Box role="rowgroup" ref={rowsContainerRef} sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, gap: 0.5 }}>
				{visibleWeeks.map(({ weekIndex, days }, renderPosition) => {
					if (days.length === 0) return null;
					const weekStart = days[0].date;
					const weekEnd = days[DAYS_IN_WEEK - 1].date;
					// Scans the RESOLVED visible days: with a non-Sunday start the 1st of the month can sit in a
					// different column than the Sunday-indexed source data would suggest.
					const firstOfMonthDay = days.find((day) => day.date.day === 1)?.date;
					// A month divider is drawn before a week that begins a new month (except the very first row).
					const showMonthDivider = renderPosition > 0 && firstOfMonthDay !== undefined;

					return (
						<Fragment key={weekStart.toISO()}>
							{showMonthDivider && firstOfMonthDay && (
								// `flex: 0 0 auto` so the divider is sized by its content and leaves the full remaining
								// height to be divided among the week rows — and pinned to ONE line (`nowrap`, with the
								// secondary label ellipsizing) so `MONTH_DIVIDER_HEIGHT_PX` describes it rather than
								// estimating it. A second line here is a row's worth of height taken from six cells
								// without the density math ever hearing about it.
								// A `rowgroup` owns ROWS, so the caption is one — a full-width spanning row, the same shape
								// a table uses for a section heading. It was `role="presentation"` before, which is not
								// `aria-hidden`: the text stayed in the accessibility tree but had no row to belong to,
								// leaving "October 2026" as an orphan between two rows.
								<Box
									role="row"
									sx={{
										display: "flex",
										flex: "0 0 auto",
										borderTop: `1px dashed ${NEUTRAL[300]}`,
										pt: 0.5,
										pl: `${WEEK_LABEL_WIDTH}px`,
									}}
								>
									<Box
										role="gridcell"
										aria-colspan={DAYS_IN_WEEK}
										sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap", gap: 1, overflow: "hidden", minWidth: 0, flex: 1 }}
									>
										<Typography sx={{ fontSize: "13px", fontWeight: 700, color: INK.PRIMARY, whiteSpace: "nowrap", flexShrink: 0 }}>
											{firstOfMonthDay.toFormat("MMMM yyyy")}
										</Typography>
										<Typography
											sx={{ fontSize: "12px", color: INK.SECONDARY, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}
										>
											{t("monthly.view.month.starts", { date: firstOfMonthDay.toFormat("ccc, MMM d") })}
										</Typography>
									</Box>
								</Box>
							)}
							<Box
								role="row"
								sx={{
									display: "grid",
									gridTemplateColumns: `${WEEK_LABEL_WIDTH}px repeat(${DAYS_IN_WEEK}, 1fr)`,
									gap: 0.5,
									// Equal share of the leftover height. `min-height` is deliberately left at `auto` so the
									// row can never shrink below its cells' legibility floor — it overflows the container
									// (whose own `minHeight: 0` keeps it bounded) and the scroll box above takes over.
									flex: "1 1 0",
								}}
							>
								<InHouseTooltip title={t("monthly.view.open.week.view")}>
									<Box
										data-testid={`monthly-week-label-${weekStart.toFormat("yyyy-MM-dd")}`}
										// A row header that is also the way into that week's own view. `rowheader` is what the
										// grid needs; the tooltip, the label and the keyboard activation are what make it an
										// affordance rather than a decoration.
										role="rowheader"
										tabIndex={0}
										aria-label={`${t("monthly.view.open.week.view")}, ${weekStart.toFormat("MMM d")} – ${weekEnd.toFormat("MMM d")}`}
										onClick={() => onWeekNumberClick(weekIndex)}
										onKeyDown={activateOnKey(() => onWeekNumberClick(weekIndex))}
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: 1,
											cursor: "pointer",
											backgroundColor: NEUTRAL[50],
											color: INK.SECONDARY,
											fontSize: "12px",
											fontWeight: 600,
											px: 1,
											"&:hover": { backgroundColor: NEUTRAL[150] },
										}}
									>
										{weekStart.toFormat("MMM d")} – {weekEnd.toFormat("MMM d")}
									</Box>
								</InHouseTooltip>
								{days.map((day) => {
									const isPast = isPastDay(day.date);
									const isHighlighted = highlightNurseId != null && nurseWorksDay(day.mergedDay, isPast, highlightNurseId);
									return (
										<MonthlyJobCell
											key={day.date.toISO()}
											day={day.mergedDay}
											date={day.date}
											sourceWeekIndex={day.sourceWeekIndex}
											sourceDayIndex={day.sourceDayIndex}
											isToday={day.date.hasSame(today, "day")}
											isFirstOfMonth={day.date.day === 1}
											isPast={isPast}
											showActual={isPast}
											isHighlighted={isHighlighted}
											visibleJobs={visibleJobs}
											isAutoScheduler={isAutoScheduler}
											onOpenDayView={onOpenDayView}
											windows={windows}
											totalsMode={totalsMode}
											filters={filters}
											density={density}
											onOpenSidebar={onOpenSidebar}
										/>
									);
								})}
							</Box>
						</Fragment>
					);
				})}
			</Box>
		</Box>
	);
};
